/**
 * MaPrimeRenovGuide
 * ─────────────────────────────────────────────────────────────────
 * Guide éditorial complet pour /particuliers/aides/maprimerenov.
 *
 * Conçu comme un long-form SEO de référence (équivalent éditorial
 * Effy / Hellio / Calculeo / Quelle Énergie), 100 % factuel et
 * pédagogique, dans le ton "warm-pedagogic" des particuliers.
 *
 * Sections :
 *  1. Hero (eyebrow, titre dégradé, lead, micro-promise, sidecard montant)
 *  2. Bandeau "En résumé" (4 puces)
 *  3. Les 3 volets officiels (Décarbonation, Ampleur, Copropriétés)
 *  4. Bénéficiaires + critères logement
 *  5. Travaux éligibles (3 sous-catégories)
 *  6. Plafonds de ressources Anah 2026 (2 tableaux IDF / hors IDF)
 *  7. Montants par geste (tableau)
 *  8. MPR Ampleur — gain DPE × profil (tableau)
 *  9. Mon Accompagnateur Rénov' (missions + prise en charge)
 * 10. Cumul avec les autres aides (chips + écrêtement)
 * 11. Démarches (timeline 5 étapes)
 * 12. FAQ (Accordion)
 * 13. CTA final
 *
 * Toutes les données sont importées depuis
 * `client/src/config/maprimerenov-content.ts` (source unique).
 *
 * SEO : injecte BreadcrumbList + Article + FAQPage + HowTo JSON-LD.
 */

import {
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Info,
  MapPin,
  ShieldAlert,
} from 'lucide-react';

import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { ArticleLd } from '../seo/jsonld/Article';
import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../seo/jsonld/FAQPage';
import { HowToLd } from '../seo/jsonld/HowTo';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import {
  CTA_HREF,
  FeatureCallout,
  SectionHeader,
  SITE_URL,
} from './_shared';
import {
  PARTICULIER_URLS,
  ParticulierCTAFinal,
  ParticulierLiveCard,
  ParticulierPageHero,
} from '../audience/particulier';
import {
  MPR_AMPLEUR_ROWS,
  MPR_BENEFICIAIRES,
  MPR_CUMULS,
  MPR_DEMARCHES,
  MPR_ECRETEMENT,
  MPR_FAQ,
  MPR_HERO,
  MPR_INCOME_HORS_IDF,
  MPR_INCOME_IDF,
  MPR_LOGEMENT_CRITERES,
  MPR_MAR_MISSIONS,
  MPR_MAR_PRISE_EN_CHARGE,
  MPR_MONTANTS_GESTES,
  MPR_SUMMARY,
  MPR_TRAVAUX,
  MPR_VOLETS,
} from '../../config/maprimerenov-content';

const PAGE_URL = `${SITE_URL}/particuliers/aides/maprimerenov`;

// ════════════════════════════════════════════════════════════════
// Helpers visuels — couleurs des 4 catégories Anah
// (Bleu, Jaune, Violet, Rose — distinctes du thème brand/accent
// pour rester lisibles dans les tableaux)
// ════════════════════════════════════════════════════════════════

const INCOME_COLOR_STYLES: Record<'bleu' | 'jaune' | 'violet' | 'rose', string> = {
  bleu: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200/70',
  jaune: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200/70',
  violet: 'bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-200/70',
  rose: 'bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200/70',
};

const INCOME_DOT_STYLES: Record<'bleu' | 'jaune' | 'violet' | 'rose', string> = {
  bleu: 'bg-sky-500',
  jaune: 'bg-amber-500',
  violet: 'bg-violet-500',
  rose: 'bg-rose-500',
};

const CUMUL_TONE_STYLES: Record<
  'brand' | 'accent' | 'sage' | 'lavender' | 'ivory',
  string
> = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  accent: 'bg-accent-50 text-accent-800 ring-accent-200/60',
  sage: 'bg-stone-100 text-stone-700 ring-stone-200/60',
  lavender: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  ivory: 'bg-ivory-100 text-stone-700 ring-stone-200/60',
};

// ════════════════════════════════════════════════════════════════
// Composant principal
// ════════════════════════════════════════════════════════════════

export function MaPrimeRenovGuide() {
  return (
    <>
      {/* SEO JSON-LD */}
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: 'Particuliers', url: `${SITE_URL}/particuliers` },
          { name: 'Aides', url: `${SITE_URL}/particuliers/aides` },
          { name: 'MaPrimeRénov’', url: PAGE_URL },
        ]}
      />
      <ArticleLd
        headline="MaPrimeRénov’ 2026 — Le guide complet pour comprendre et calculer votre aide"
        url={PAGE_URL}
        datePublished="2026-01-01"
        dateModified={new Date().toISOString().slice(0, 10)}
      />
      <FAQPageLd
        items={MPR_FAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />
      <HowToLd
        name="Comment demander MaPrimeRénov’ en 2026"
        description="Les 5 étapes pour faire votre demande MaPrimeRénov’ auprès de l'Anah."
        steps={MPR_DEMARCHES.map((d) => ({ name: d.title, text: d.description }))}
      />

      <HeroSection />
      <SummarySection />
      <VoletsSection />
      <BeneficiairesSection />
      <TravauxSection />
      <IncomeBandsSection />
      <MontantsGestesSection />
      <AmpleurSection />
      <MarSection />
      <CumulSection />
      <DemarchesSection />
      <FaqSection />
      <FinalCtaSection />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// 1. HERO
// ════════════════════════════════════════════════════════════════

function HeroSection() {
  return (
    <ParticulierPageHero
      breadcrumb={[
        { name: 'Aides', href: '/particuliers/aides' },
        { name: "MaPrimeRénov\u2019" },
      ]}
      eyebrow={MPR_HERO.eyebrow}
      headline={MPR_HERO.titlePrimary}
      headlineAccent={MPR_HERO.titleAccent}
      subheadline={MPR_HERO.lead}
      primaryCta={{ label: 'Calculer ma prime en 2 min', href: CTA_HREF }}
      secondaryCta={{ label: 'Voir les travaux éligibles', href: '#travaux-eligibles' }}
      microPromise={MPR_HERO.microPromise}
      sideSlot={<MprAmountSideCard />}
    />
  );
}

function MprAmountSideCard() {
  return (
    <ParticulierLiveCard eyebrow="Montant cumulable">
      <p className="bg-gradient-to-br from-accent-700 via-accent-600 to-brand-600 bg-clip-text text-4xl font-black leading-none tracking-tight text-transparent">
        {MPR_HERO.amountHeadline}
      </p>
      <p className="mt-3 text-[13px] leading-relaxed text-stone-500">
        {MPR_HERO.amountSubline}
      </p>

      <div className="mt-5 border-t border-stone-100 pt-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400">
          Étapes du dossier
        </p>
        <ol className="mt-3 space-y-2 text-[13px] text-stone-700">
          {[
            'Simulation des aides',
            'Devis artisan RGE',
            'Dépôt sur maprimerenov.gouv.fr',
            'Notification & travaux',
            'Versement Anah',
          ].map((step, idx) => (
            <li key={step} className="flex items-start gap-2">
              <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-800">
                {idx + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
      </div>
    </ParticulierLiveCard>
  );
}

// ════════════════════════════════════════════════════════════════
// 2. EN RÉSUMÉ
// ════════════════════════════════════════════════════════════════

function SummarySection() {
  return (
    <section className="border-y border-stone-200/70 bg-stone-50/60 py-10 md:py-14">
      <Container>
        <div className="rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle md:p-8">
          <div className="mb-5 flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
            </span>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-500">
              En résumé
            </p>
          </div>

          <ul className="grid gap-3 md:grid-cols-2">
            {MPR_SUMMARY.map((point) => (
              <li
                key={point.text}
                className="flex items-start gap-2.5 rounded-xl border border-stone-100 bg-stone-50/50 p-3.5"
              >
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-brand-500"
                  strokeWidth={2.4}
                />
                <span className="text-[13.5px] leading-relaxed text-stone-700">
                  {point.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 3. LES 3 VOLETS OFFICIELS
// ════════════════════════════════════════════════════════════════

function VoletsSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Les trois volets officiels"
          title="MaPrimeRénov’ se décline en trois parcours"
          subtitle="Selon l'ampleur de vos travaux, vous êtes orienté(e) vers l'un des trois volets — chacun avec ses règles, ses plafonds et ses modalités."
        />

        <div className="grid gap-5 md:grid-cols-3">
          {MPR_VOLETS.map((volet) => {
            const Icon = getIcon(volet.iconName);
            return (
              <article
                key={volet.id}
                className="flex flex-col gap-4 rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle transition-shadow duration-300 hover:shadow-medium"
              >
                <div className="flex items-start justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-stone-400">
                    {volet.caption}
                  </span>
                </div>

                <div>
                  <h3 className="font-display text-lg font-bold text-ink">
                    {volet.title}
                  </h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-stone-600">
                    {volet.description}
                  </p>
                </div>

                <div className="mt-auto space-y-2.5 border-t border-stone-100 pt-4 text-[12.5px]">
                  <div className="flex items-start gap-1.5">
                    <span className="font-semibold text-stone-500">Public visé :</span>
                    <span className="text-stone-700">{volet.audience}</span>
                  </div>
                  <p className="rounded-lg bg-brand-50/60 p-2.5 text-[12.5px] leading-relaxed text-brand-800 ring-1 ring-inset ring-brand-100">
                    <span className="font-semibold">À retenir : </span>
                    {volet.highlight}
                  </p>
                </div>

                {volet.href ? (
                  <a
                    href={volet.href}
                    className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-700 transition-transform duration-150 ease-out hover:translate-x-0.5"
                  >
                    En savoir plus
                    <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
                  </a>
                ) : null}
              </article>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 4. BÉNÉFICIAIRES + CRITÈRES LOGEMENT
// ════════════════════════════════════════════════════════════════

function BeneficiairesSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Qui peut en bénéficier ?"
          title="MaPrimeRénov’ s'adresse aux propriétaires"
          subtitle="L'aide est ouverte aux propriétaires occupants, bailleurs et acquéreurs sous compromis, ainsi qu'aux syndicats de copropriétaires. Les locataires ne peuvent pas en faire la demande directement."
        />

        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* Bénéficiaires */}
          <div className="grid gap-3 sm:grid-cols-2">
            {MPR_BENEFICIAIRES.map((b) => {
              const Icon = getIcon(b.iconName);
              return (
                <div
                  key={b.label}
                  className="rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  <p className="mt-3 text-[14px] font-semibold text-ink">
                    {b.label}
                  </p>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-stone-600">
                    {b.detail}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Critères logement */}
          <div className="rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle md:p-8">
            <div className="mb-4 flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-accent-50 text-accent-700 ring-1 ring-inset ring-accent-200/60">
                <MapPin className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-500">
                Critères du logement
              </p>
            </div>
            <ul className="space-y-3">
              {MPR_LOGEMENT_CRITERES.map((criterion) => (
                <li key={criterion} className="flex items-start gap-2.5">
                  <CheckCircle2
                    className="mt-0.5 h-4 w-4 shrink-0 text-brand-500"
                    strokeWidth={2.2}
                  />
                  <span className="text-[13.5px] leading-relaxed text-stone-700">
                    {criterion}
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-5 flex items-start gap-2.5 rounded-xl bg-stone-100/60 p-3.5 text-[12.5px] leading-relaxed text-stone-600 ring-1 ring-inset ring-stone-200">
              <Info
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500"
                strokeWidth={1.8}
              />
              En Outre-mer, l&rsquo;obligation de fournir un DPE et d&rsquo;installer un
              chauffage / ECS décarboné est levée.
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 5. TRAVAUX ÉLIGIBLES
// ════════════════════════════════════════════════════════════════

function TravauxSection() {
  return (
    <section id="travaux-eligibles" className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Travaux éligibles 2026"
          title="Trois grandes familles de travaux financés"
          subtitle="Tous les travaux doivent être réalisés par un artisan certifié RGE (Reconnu Garant de l'Environnement) pour la catégorie d'opération concernée."
        />

        <div className="grid gap-5 md:grid-cols-3">
          {MPR_TRAVAUX.map((cat) => {
            const Icon = getIcon(cat.iconName);
            return (
              <article
                key={cat.title}
                className="flex flex-col gap-4 rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <h3 className="font-display text-lg font-bold text-ink">
                  {cat.title}
                </h3>
                <ul className="space-y-2">
                  {cat.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-[13px] leading-relaxed text-stone-700"
                    >
                      <CheckCircle2
                        className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-500"
                        strokeWidth={2.2}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {cat.note ? (
                  <p className="mt-auto flex items-start gap-2 rounded-lg bg-amber-50/70 p-2.5 text-[12px] leading-relaxed text-amber-900 ring-1 ring-inset ring-amber-200/70">
                    <ShieldAlert
                      className="mt-0.5 h-3.5 w-3.5 shrink-0"
                      strokeWidth={2}
                    />
                    <span>{cat.note}</span>
                  </p>
                ) : null}
              </article>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Button asChild variant="outline" size="md">
            <a href="/cee/fiches?secteur=BAR">
              Voir toutes les fiches CEE résidentielles
              <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <span className="text-[12.5px] text-stone-500">
            La grande majorité des travaux éligibles à MaPrimeRénov&rsquo; le sont
            également à la prime énergie (CEE).
          </span>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 6. PLAFONDS DE RESSOURCES ANAH 2026
// ════════════════════════════════════════════════════════════════

function IncomeBandsSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Plafonds de ressources Anah 2026"
          title="Quatre catégories de revenus déterminent votre prime"
          subtitle="MaPrimeRénov’ répartit les foyers en 4 profils — Bleu, Jaune, Violet, Rose — en fonction du revenu fiscal de référence (RFR) et de la composition du foyer. Le profil détermine à la fois les montants et les taux d'aide."
        />

        {/* Légende des 4 catégories */}
        <div className="mb-8 flex flex-wrap gap-2">
          {(['bleu', 'jaune', 'violet', 'rose'] as const).map((color) => (
            <span
              key={color}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold',
                INCOME_COLOR_STYLES[color],
              )}
            >
              <span className={cn('h-2 w-2 rounded-full', INCOME_DOT_STYLES[color])} />
              {color === 'bleu' && 'Bleu · Très modestes'}
              {color === 'jaune' && 'Jaune · Modestes'}
              {color === 'violet' && 'Violet · Intermédiaires'}
              {color === 'rose' && 'Rose · Supérieurs'}
            </span>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <IncomeTable
            title="Île-de-France"
            subtitle="Plafonds de revenu fiscal de référence (RFR n-1 ou n-2)"
            bands={MPR_INCOME_IDF}
          />
          <IncomeTable
            title="Autres régions"
            subtitle="France métropolitaine hors IDF + Outre-mer"
            bands={MPR_INCOME_HORS_IDF}
          />
        </div>

        <p className="mt-6 flex items-start gap-2.5 rounded-xl bg-stone-100/60 p-4 text-[12.5px] leading-relaxed text-stone-600 ring-1 ring-inset ring-stone-200">
          <Info
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500"
            strokeWidth={1.8}
          />
          Barème Anah au 1ᵉʳ janvier 2026, revalorisé annuellement. Les ménages au
          profil Rose (revenus supérieurs) restent éligibles à MaPrimeRénov&rsquo; Ampleur
          (parcours accompagné) avec des montants minorés, mais sont exclus de
          MaPrimeRénov&rsquo; Décarbonation (parcours par geste).
        </p>
      </Container>
    </section>
  );
}

interface IncomeTableProps {
  title: string;
  subtitle: string;
  bands: typeof MPR_INCOME_IDF;
}

function IncomeTable({ title, subtitle, bands }: IncomeTableProps) {
  return (
    <div className="overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-subtle">
      <header className="border-b border-stone-100 bg-stone-50/60 px-5 py-4">
        <p className="font-display text-base font-bold text-ink">{title}</p>
        <p className="mt-0.5 text-[12px] text-stone-500">{subtitle}</p>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="bg-stone-50/40 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-stone-500">
              <th className="px-3 py-3 font-semibold">Foyer</th>
              <th className="px-3 py-3 font-semibold text-sky-700">Bleu</th>
              <th className="px-3 py-3 font-semibold text-amber-700">Jaune</th>
              <th className="px-3 py-3 font-semibold text-violet-700">Violet</th>
              <th className="px-3 py-3 font-semibold text-rose-700">Rose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {(['p1', 'p2', 'p3', 'p4', 'p5'] as const).map((key, idx) => (
              <tr key={key} className="text-stone-700">
                <td className="px-3 py-2.5 font-medium text-stone-600">
                  {idx + 1} {idx === 0 ? 'personne' : 'personnes'}
                </td>
                {bands.map((band) => (
                  <td key={band.label} className="px-3 py-2.5 whitespace-nowrap">
                    {band[key]}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-stone-50/40 text-stone-600">
              <td className="px-3 py-2.5 text-[11.5px] font-medium italic">
                Par personne supplémentaire
              </td>
              {bands.map((band) => (
                <td
                  key={band.label}
                  className="px-3 py-2.5 whitespace-nowrap text-[11.5px] italic"
                >
                  {band.perExtra}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 7. MONTANTS PAR GESTE (MPR Décarbonation)
// ════════════════════════════════════════════════════════════════

function MontantsGestesSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Montants par geste"
          title="Combien pour quel travail ? (MaPrimeRénov’ Décarbonation)"
          subtitle="Plafonds maximaux accordés en parcours par geste. Le montant exact dépend de votre catégorie de revenus (Bleu, Jaune, Violet) — les ménages Rose sont exclus du parcours par geste."
        />

        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-subtle">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50/60 text-left text-[10.5px] font-bold uppercase tracking-[0.1em] text-stone-500">
                  <th className="px-5 py-3.5 font-semibold">Type de travaux</th>
                  <th className="px-5 py-3.5 font-semibold">Plafond maximal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {MPR_MONTANTS_GESTES.map((row) => (
                  <tr
                    key={row.travaux}
                    className="transition-colors hover:bg-stone-50/40"
                  >
                    <td className="px-5 py-3 text-stone-800">
                      {row.travaux}
                      {row.note ? (
                        <span className="ml-1.5 text-[11.5px] italic text-stone-500">
                          ({row.note})
                        </span>
                      ) : null}
                    </td>
                    <td className="px-5 py-3 font-semibold text-brand-700">
                      {row.plafond}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-5 text-[12.5px] leading-relaxed text-stone-500">
          Source : barème MaPrimeRénov&rsquo; 2026 — Anah. Pour un chiffrage personnalisé
          intégrant votre profil et vos travaux, lancez le calculateur.
        </p>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 8. MPR AMPLEUR (RÉNOVATION GLOBALE)
// ════════════════════════════════════════════════════════════════

function AmpleurSection() {
  return (
    <section className="border-t border-stone-100 bg-gradient-to-b from-stone-50/60 to-paper py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="MaPrimeRénov’ Ampleur"
          title="La rénovation globale, davantage récompensée"
          subtitle="Pour les chantiers d'envergure permettant un gain d'au moins 2 classes au DPE, MaPrimeRénov’ Ampleur ouvre des montants nettement supérieurs au parcours par geste. Le passage par Mon Accompagnateur Rénov’ est obligatoire."
        />

        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-subtle">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50/60 text-left text-[10.5px] font-bold uppercase tracking-[0.1em] text-stone-500">
                  <th className="px-4 py-3.5 font-semibold">Gain DPE</th>
                  <th className="px-4 py-3.5 font-semibold">Dépenses HT plafonnées</th>
                  <th className="px-4 py-3.5 font-semibold text-sky-700">Bleu</th>
                  <th className="px-4 py-3.5 font-semibold text-amber-700">Jaune</th>
                  <th className="px-4 py-3.5 font-semibold text-violet-700">Violet</th>
                  <th className="px-4 py-3.5 font-semibold text-rose-700">Rose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {MPR_AMPLEUR_ROWS.map((row) => (
                  <tr
                    key={row.gain}
                    className="transition-colors hover:bg-stone-50/40"
                  >
                    <td className="px-4 py-3 font-medium text-stone-800">
                      {row.gain}
                    </td>
                    <td className="px-4 py-3 text-stone-700">{row.depensesHt}</td>
                    <td className="px-4 py-3 font-semibold text-sky-700">
                      {row.bleu}
                    </td>
                    <td className="px-4 py-3 font-semibold text-amber-700">
                      {row.jaune}
                    </td>
                    <td className="px-4 py-3 font-semibold text-violet-700">
                      {row.violet}
                    </td>
                    <td className="px-4 py-3 font-semibold text-rose-700">
                      {row.rose}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-3">
          <FeatureCallout
            iconName="layers"
            title="Minimum 2 gestes d’isolation"
            text="Au moins deux travaux d'isolation thermique parmi murs, toiture, sols, parois vitrées."
          />
          <FeatureCallout
            iconName="gauge"
            title="Saut de classes au DPE"
            text="Gain d'au moins 2 classes au DPE certifié par audit énergétique post-travaux."
          />
          <FeatureCallout
            iconName="handshake"
            title="Mon Accompagnateur Rénov’"
            text="Passage obligatoire par un MAR agréé pour cadrer le projet et déposer le dossier."
          />
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 9. MON ACCOMPAGNATEUR RÉNOV' (MAR)
// ════════════════════════════════════════════════════════════════

function MarSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Mon Accompagnateur Rénov’"
          title="Le tiers de confiance pour les rénovations d&rsquo;ampleur"
          subtitle="Mon Accompagnateur Rénov’ (MAR) est un opérateur agréé par l'État, indépendant des artisans et des fournisseurs. Il sécurise votre projet de bout en bout : audit, choix des travaux, dossier MPR Ampleur, suivi de chantier."
        />

        <div className="grid gap-10 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          {/* Missions du MAR */}
          <div className="grid gap-3 sm:grid-cols-2">
            {MPR_MAR_MISSIONS.map((mission) => {
              const Icon = getIcon(mission.iconName);
              return (
                <div
                  key={mission.title}
                  className="rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-accent-50 text-accent-800 ring-1 ring-inset ring-accent-200/60">
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  <p className="mt-3 text-[14px] font-semibold text-ink">
                    {mission.title}
                  </p>
                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-stone-600">
                    {mission.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Prise en charge du coût MAR */}
          <div className="rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle md:p-7">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-500">
              Prise en charge du coût MAR
            </p>
            <h3 className="mt-2 font-display text-lg font-bold text-ink">
              Plafond de dépenses éligibles : 2 000 € TTC
            </h3>
            <p className="mt-2 text-[12.5px] leading-relaxed text-stone-600">
              MaPrimeRénov&rsquo; prend en charge tout ou partie des honoraires de votre
              MAR, selon votre catégorie de revenus.
            </p>
            <ul className="mt-5 space-y-2.5">
              {MPR_MAR_PRISE_EN_CHARGE.map((row) => (
                <li
                  key={row.profil}
                  className="flex items-center justify-between gap-3 rounded-lg border border-stone-100 px-3.5 py-2.5"
                >
                  <span className="flex items-center gap-2 text-[13px] text-stone-700">
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full',
                        INCOME_DOT_STYLES[row.color],
                      )}
                    />
                    {row.profil}
                  </span>
                  <span
                    className={cn(
                      'rounded-md px-2 py-0.5 text-[12px] font-bold',
                      INCOME_COLOR_STYLES[row.color],
                    )}
                  >
                    {row.taux}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 10. CUMUL AVEC LES AUTRES AIDES
// ════════════════════════════════════════════════════════════════

function CumulSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Cumul avec les autres aides"
          title="MaPrimeRénov’ se cumule avec 4 autres dispositifs"
          subtitle="Notre rôle : croiser tous les dispositifs cumulables pour réduire votre reste à charge au maximum, dans le respect du plafond d'écrêtement réglementaire."
        />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {MPR_CUMULS.map((c) => {
            const Icon = getIcon(c.iconName);
            const Tag = c.href ? 'a' : 'div';
            return (
              <Tag
                key={c.aide}
                {...(c.href ? { href: c.href } : {})}
                className={cn(
                  'group flex flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle',
                  c.href && 'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-medium',
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset',
                      CUMUL_TONE_STYLES[c.tone],
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  {c.href ? (
                    <ArrowRight
                      className="h-3.5 w-3.5 text-stone-400 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-brand-600"
                      strokeWidth={2.4}
                    />
                  ) : null}
                </div>
                <p className="text-[14px] font-semibold text-ink">{c.aide}</p>
                <p className="text-[12.5px] leading-relaxed text-stone-600">
                  {c.description}
                </p>
              </Tag>
            );
          })}
        </div>

        {/* Plafond d'écrêtement */}
        <div className="mt-8 rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle md:p-8">
          <div className="mb-4 flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/70">
              <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2} />
            </span>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-500">
              Plafond d&rsquo;écrêtement
            </p>
          </div>
          <p className="text-[14px] leading-relaxed text-stone-700">
            Le cumul total d&rsquo;aides ne peut pas dépasser un certain pourcentage des
            dépenses HT, défini selon votre profil :
          </p>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {MPR_ECRETEMENT.map((e) => (
              <li
                key={e.profil}
                className="flex items-center justify-between gap-3 rounded-lg border border-stone-100 bg-stone-50/40 px-3.5 py-2.5"
              >
                <span className="flex items-center gap-2 text-[12.5px] text-stone-700">
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      INCOME_DOT_STYLES[e.color],
                    )}
                  />
                  {e.profil}
                </span>
                <span className="text-[12px] font-bold text-stone-800">{e.taux}</span>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 11. DÉMARCHES (TIMELINE)
// ════════════════════════════════════════════════════════════════

function DemarchesSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Comment faire votre demande"
          title="5 étapes pour obtenir MaPrimeRénov’ en 2026"
          subtitle="L'ordre des étapes est strict : déposer le dossier AVANT de signer le devis est la règle d'or — sinon votre demande est automatiquement refusée."
        />

        <ol className="grid gap-4 md:gap-3">
          {MPR_DEMARCHES.map((step, idx) => {
            const Icon = getIcon(step.iconName);
            const isLast = idx === MPR_DEMARCHES.length - 1;
            return (
              <li
                key={step.step}
                className="relative flex items-start gap-5 rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle md:p-6"
              >
                {/* Number + icon */}
                <div className="flex shrink-0 flex-col items-center gap-2">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-600 to-accent-600 text-base font-bold text-white shadow-glow">
                    {step.step}
                  </span>
                  {!isLast ? (
                    <span
                      aria-hidden
                      className="hidden h-full w-px flex-1 bg-stone-200 md:block"
                    />
                  ) : null}
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                      <Icon className="h-4 w-4" strokeWidth={1.7} />
                    </span>
                    <h3 className="font-display text-base font-bold text-ink md:text-lg">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-stone-600">
                    {step.description}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="mt-8 flex flex-wrap items-center gap-3 rounded-2xl border border-brand-200 bg-brand-50/50 p-5">
          <p className="text-[13.5px] leading-relaxed text-brand-900">
            <span className="font-bold">Notre rôle chez Solution Éco France :</span>{' '}
            simuler vos aides, vous présenter un artisan RGE de notre réseau,
            intégrer la prime énergie (CEE) en déduction directe du devis et vous
            accompagner dans le dépôt de votre dossier MaPrimeRénov&rsquo;.
          </p>
          <Button asChild variant="primary" size="md">
            <a href={CTA_HREF}>
              Calculer ma prime
              <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 12. FAQ
// ════════════════════════════════════════════════════════════════

function FaqSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container size="narrow">
        <SectionHeader
          eyebrow="FAQ"
          title="Vos questions sur MaPrimeRénov’"
          subtitle="Les questions les plus fréquentes posées par les propriétaires en 2026, traitées une à une avec une réponse claire et factuelle."
          align="center"
        />

        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-subtle">
          <Accordion type="single" collapsible className="divide-y divide-stone-100">
            {MPR_FAQ.map((item, idx) => (
              <AccordionItem
                key={item.question}
                value={`mpr-faq-${idx}`}
                className="border-0 px-5"
              >
                <AccordionTrigger className="text-[15px]">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-[14px] leading-relaxed text-stone-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <p className="mt-6 flex items-start gap-2.5 text-center text-[12.5px] leading-relaxed text-stone-500">
          <ExternalLink
            className="mt-0.5 h-3.5 w-3.5 shrink-0"
            strokeWidth={1.8}
          />
          Source officielle :{' '}
          <a
            href="https://www.maprimerenov.gouv.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-brand-700 hover:underline"
          >
            maprimerenov.gouv.fr
          </a>{' '}
          (Anah).
        </p>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 13. CTA FINAL
// ════════════════════════════════════════════════════════════════

function FinalCtaSection() {
  return (
    <ParticulierCTAFinal
      badge="Calcul personnalisé · 2 min"
      title="Combien pouvez-vous toucher pour votre projet ?"
      description={
        <>
          Décrivez votre logement, vos revenus et vos travaux. Notre calculateur
          croise MaPrimeRénov&rsquo;, la prime énergie (CEE), l&rsquo;éco-PTZ, la
          TVA 5,5 % et les aides locales pour vous donner un montant prévisionnel
          fiable. Sans inscription, sans engagement.
        </>
      }
      primaryCta={{ label: 'Calculer mes aides', href: CTA_HREF }}
      secondaryCta={{ label: 'Parler à un conseiller', href: PARTICULIER_URLS.contact }}
      footnote={"Aucune carte bancaire · aucune inscription · MaPrimeRénov\u2019 versée directement par l\u2019Anah après dépôt."}
    />
  );
}
